import React, { useContext, useState, useCallback, useEffect } from 'react';
import { Key, RefreshCw, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/Badge';
import { CredentialsApi, PaginatedPublicLeakList, PublicLeak } from 'api-client';
import { useTeams } from '../../contexts/TeamsContext';
import { AuthContext } from '../../contexts/AuthContext';
import { getApiConfiguration } from '../../api/utils';
import { TablePagination } from '../../components/ui/TablePagination';
import { PublicLeaksFilters } from '../../components/data-protection/PublicLeaksFilters';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import ExportActionBtn from '@/components/ui/ExportActionBtn';

/**
 * Props for the PublicLeaksList component
 */
interface PublicLeaksListProps {
  /** Public leaks data to display (optional, will fetch from API if not provided) */
  leaks?: PublicLeak[];
  /** Current page number for pagination (optional) */
  currentPage?: number;
  /** Function to handle page changes (optional) */
  onPageChange?: (page: number) => void;
  /** Total number of leaks for pagination (optional) */
  totalLeaks?: number;
  /** Number of items per page (optional) */
  itemsPerPage?: number;
  /** Pagination information object (optional) */
  paginationInfo?: {
    count: number;
    next: string | null;
    previous: string | null;
  };
  /** Whether to show the header section (optional) */
  showHeader?: boolean;
  /** Whether to show the filters section (optional) */
  showFilters?: boolean;
}

/**
 * PublicLeaksList component that can either use provided data or fetch from API
 */
const PublicLeaksList: React.FC<PublicLeaksListProps> = ({
  leaks: providedLeaks,
  currentPage: providedCurrentPage,
  onPageChange: providedOnPageChange,
  totalLeaks,
  itemsPerPage = 10,
  paginationInfo: providedPaginationInfo,
  showHeader = true,
  showFilters = true,
}) => {
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedSource, setSelectedSource] = useState(searchParams.get('source') || 'all');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leaks, setLeaks] = useState<PublicLeak[]>(providedLeaks || []);
  const [paginationInfo, setPaginationInfo] = useState<{
    count: number;
    next: string | null;
    previous: string | null;
  }>(providedPaginationInfo || { count: totalLeaks || 0, next: null, previous: null });

  // Handle page changes, either using provided handler or internal state
  const handlePageChange = (page: number) => {
    if (providedOnPageChange) {
      providedOnPageChange(page);
    } else {
      setCurrentPage(page);
    }

    const url = buildUrlWithParams({
      page,
      search: searchQuery,
      source: selectedSource,
    });
  
    // Navigate to the new URL
    navigate(url, {
      state: { pageChange: true, timestamp: Date.now() }
    });
  };

  const handleReset = () => {
    setSearchQuery('');
    setSelectedSource('');
  
    navigate(location.pathname, {
      state: { reset: true, timestamp: Date.now() }
    });
  };

  useEffect(() => {
    // Only update if the change is from navigation (not from local state changes)
    const pageParam = searchParams.get('page');
    const searchParam = searchParams.get('search');
    const sourceParam = searchParams.get('source');
    
    // Update page if it changed
    if (pageParam) {
      const newPage = parseInt(pageParam, 10);
      if (newPage !== currentPage) {
        setCurrentPage(newPage);
      }
    } else if (currentPage !== 1) {
      // If page param is removed, reset to page 1
      setCurrentPage(1);
    }
    
    // Update search query if it changed
    if (searchParam !== searchQuery) {
      setSearchQuery(searchParam || '');
    }
    
    // Update source if it changed
    if (sourceParam !== selectedSource) {
      setSelectedSource(sourceParam || '');
    }
  }, [searchParams]);

  const buildUrlWithParams = (params: Record<string, any>) => {
    const urlParams = new URLSearchParams();
    
    // Add page parameter if greater than 1
    if (params.page && params.page > 1) {
      urlParams.set('page', params.page.toString());
    }
    
    // Add search query if present
    if (params.search) {
      urlParams.set('search', params.search);
    }
    
    // Add source if present
    if (params.source) {
      urlParams.set('source', params.source);
    }
    
    return `${location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
  };
  
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    
    // Construct the URL with updated parameters
    const newUrl = buildUrlWithParams({
      page: 1, // Explicitly set page to 1
      search: query,
      source: selectedSource,
    });
    
    // Navigate to the new URL
    navigate(newUrl, { state: { timestamp: new Date().getTime() } });
  };
  
  const handleSourceChange = (source: string) => {
    setSelectedSource(source);
    
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    
    // Construct the URL with updated parameters
    const newUrl = buildUrlWithParams({
      page: 1, // Explicitly set page to 1
      search: searchQuery,
      source: source,
    });
    
    // Navigate to the new URL
    navigate(newUrl, { state: { timestamp: new Date().getTime() } });
  };


  const fetchLeaks = useCallback(async () => {
    // Skip API call if data is provided externally
    if (providedLeaks) return;
    
    if (!currentTeam?.slug || !token) return;

    setIsLoading(true);
    setError(null);

    try {
      const api = new CredentialsApi(getApiConfiguration(token));
      const response = await api.publicLeaksList({
        teamSlug: currentTeam.slug,
        page: currentPage,
        search: searchQuery,
        source: selectedSource === 'all' ? undefined : selectedSource,
        pageSize: 10,
      });
      setLeaks(response.results);
      setPaginationInfo({
        count: response.count,
        next: response.next || null,
        previous: response.previous || null
      });
    } catch (err) {
      console.error('Error fetching public leaks:', err);
      setError('Failed to load public leaks');
    } finally {
      setIsLoading(false);
    }
  }, [currentTeam?.slug, token, currentPage, searchQuery, selectedSource, providedLeaks]);

  const fetchAllLeaks = async(totalLeaksCount: number) => {
    if (!currentTeam?.slug || !token) return;

    try {
      const api = new CredentialsApi(getApiConfiguration(token));
      const response = await api.publicLeaksList({
        teamSlug: currentTeam.slug,
        search: searchQuery,
        source: selectedSource === 'all' ? undefined : selectedSource,
        pageSize: totalLeaksCount
      });
      return response.results;
    } catch (err) {
      toast.error('Failed to Download all leaks');
      return [];
    }
  }

  useEffect(() => {
    // Update local state if props change
    if (providedLeaks) {
      setLeaks(providedLeaks);
    }
    if (providedCurrentPage) {
      setCurrentPage(providedCurrentPage);
    }
    if (providedPaginationInfo) {
      setPaginationInfo(providedPaginationInfo);
    } else if (totalLeaks !== undefined) {
      setPaginationInfo(prev => ({ ...prev, count: totalLeaks }));
    }
  }, [providedLeaks, providedCurrentPage, providedPaginationInfo, totalLeaks]);

  useEffect(() => {
    // Only fetch data if not provided externally
    if (!providedLeaks) {
      fetchLeaks();
    }
  }, [fetchLeaks, providedLeaks]);

  return (
    <div className="space-y-6">
      {/* Header - Only show if showHeader is true */}
      {showHeader && (
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Key className="h-8 w-8 text-blue-500" />
            <div>
              <h1 className="text-2xl font-bold text-white">Public Leaks</h1>
              <p className="text-sm text-gray-400">Comprehensive list of detected credential leaks</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <ExportActionBtn
              fetchAllData={() => fetchAllLeaks(paginationInfo.count)}
              filename="public_leaks"
              disabled={leaks?.length === 0}
            />
            <Button
              onClick={fetchLeaks}
              disabled={isLoading || !!providedLeaks}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              {isLoading ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </div>
      )}

      {/* Filters - Only show if showFilters is true */}
      {showFilters && (
        <PublicLeaksFilters
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          selectedSource={selectedSource}
          onSourceChange={handleSourceChange}
          onReset={handleReset}
        />
      )}

      {/* Leaks Table */}
      <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 overflow-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Email</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Password</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Source</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Detected</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-400">
                  Loading...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-red-400">
                  {error}
                </td>
              </tr>
            ) : leaks.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-400">
                  No public leaks found
                </td>
              </tr>
            ) : (
              leaks.map((leak) => (
                <tr key={leak.id} className="group hover:bg-gray-800/30">
                  <td className="px-6 py-4 cursor-pointer" onClick={() => navigate(`/data-protection/public-leaks/${leak.id}`)}>
                    <span className="text-sm text-white">{leak.identifier}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-white">{leak.password}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-white">{leak.sourceName}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-300">
                      {leak.foundDate ? new Date(leak.foundDate).toLocaleString() : 'N/A'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {!isLoading && !error && paginationInfo.count > 0 && (
          <TablePagination
            currentPage={currentPage}
            onPageChange={handlePageChange}
            totalItems={paginationInfo.count}
            itemsPerPage={itemsPerPage}
            paginationInfo={paginationInfo}
            itemName="leaks"
          />
        )}
      </div>
    </div>
  );
};

export default PublicLeaksList;